import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Modal, Form, Alert } from 'react-bootstrap';
import { getCategories, createCategory, updateTickerCategory, updateAccountTickerCategory, updateWatchlistCategory, updateLeaderCategory, updateReadylistCategory, updateEpsCategory, updateVcpCategory, updateBasingCategory, updateThemeTicker, updateStopoutCategory, updateSteroidCategory, updateEtfCategory, updateAntCategory, updateNewIpoCategory } from '../services/api';

const RECENT_CATEGORIES_KEY = 'recentCategorySelections';
const MAX_RECENT_CATEGORIES = 5;

const loadRecentCategories = () => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const stored = localStorage.getItem(RECENT_CATEGORIES_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.map(id => id.toString()) : [];
  } catch (error) {
    console.error('Failed to load recent categories from storage', error);
    return [];
  }
};

const CategorySelect = ({ tickerId, currentCategory, onCategoryUpdate, accountId, isWatchlist, isLeaders, isReadylist, isEPS, isVcp, isBasing, isTheme, themeName, isStopouts, isSteroids, isEtfs, isAnts, isNewIpos }) => {
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const [recentCategoryIds, setRecentCategoryIds] = useState(() => loadRecentCategories());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(RECENT_CATEGORIES_KEY, JSON.stringify(recentCategoryIds));
    } catch (error) {
      console.error('Failed to persist recent categories', error);
    }
  }, [recentCategoryIds]);

  useEffect(() => {
    fetchCategories();
    // Intentionally mount-only: fetchCategories fetches the full category list from the
    // server, so re-running it on every `currentCategory` prop change would be wasteful.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus search input when modal opens
  useEffect(() => {
    if (showModal && !showCreateForm && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [showModal, showCreateForm]);

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      setCategories(response.data);
      if (currentCategory) {
        setSelectedCategory(currentCategory.category_id?.toString() || '');
        setSearchTerm(currentCategory.name || '');
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        stack: err.stack
      });
      setError('Failed to load categories');
    }
  };

  const handleShow = () => setShowModal(true);
  const handleClose = () => {
    setShowModal(false);
    setError('');
    setShowCreateForm(false);
    setNewCategoryName('');
    setNewCategoryDescription('');
    setSearchTerm('');
    setShowDropdown(false);
  };

  const recordRecentCategory = (categoryId) => {
    if (!categoryId) {
      return;
    }
    setRecentCategoryIds((prev) => {
      const idString = categoryId.toString();
      const next = [idString, ...prev.filter((id) => id !== idString)];
      return next.slice(0, MAX_RECENT_CATEGORIES);
    });
  };

  const handleCreateCategory = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await createCategory({
        name: newCategoryName,
        description: newCategoryDescription
      });

      // Add the new category to the list
      setCategories([...categories, response.data]);
      setSelectedCategory(response.data.category_id.toString());
      setSearchTerm(response.data.name);
      setShowCreateForm(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      let response;
      const categoryId = selectedCategory ? parseInt(selectedCategory) : null;

      if (isReadylist) {
        response = await updateReadylistCategory(tickerId, categoryId);
      } else if (isLeaders) {
        response = await updateLeaderCategory(tickerId, categoryId);
      } else if (isEPS) {
        response = await updateEpsCategory(tickerId, categoryId);
      } else if (isVcp) {
        response = await updateVcpCategory(tickerId, categoryId);
      } else if (isBasing) {
        response = await updateBasingCategory(tickerId, categoryId);
      } else if (isTheme) {
        if (!themeName) {
          throw new Error('Theme name is required for category updates');
        }
        response = await updateThemeTicker(themeName, tickerId, { category_id: categoryId });
      } else if (isWatchlist) {
        response = await updateWatchlistCategory(tickerId, categoryId);
      } else if (isAnts) {
        response = await updateAntCategory(tickerId, categoryId);
      } else if (isNewIpos) {
        response = await updateNewIpoCategory(tickerId, categoryId);
      } else if (isStopouts) {
        response = await updateStopoutCategory(tickerId, categoryId);
      } else if (isSteroids) {
        response = await updateSteroidCategory(tickerId, categoryId);
      } else if (isEtfs) {
        response = await updateEtfCategory(tickerId, categoryId);
      } else if (accountId) {
        response = await updateAccountTickerCategory(accountId, tickerId, categoryId);
      } else {
        response = await updateTickerCategory(tickerId, categoryId);
      }
      
      onCategoryUpdate(response.data);
      if (categoryId) {
        recordRecentCategory(categoryId);
      }
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update category');
    } finally {
      setLoading(false);
    }
  };

  // Function to prepare for creating a new category with the search term
  const handleCreateNewWithSearch = () => {
    setNewCategoryName(searchTerm);
    setShowCreateForm(true);
    setShowDropdown(false);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    
    // Find if the typed value matches any existing category
    const matchingCategory = categories.find(
      cat => cat.name.toLowerCase() === e.target.value.toLowerCase()
    );
    
    if (matchingCategory) {
      setSelectedCategory(matchingCategory.category_id.toString());
    } else if (e.target.value === '') {
      setSelectedCategory('');
    }
  };

  // Handle category selection from dropdown
  const handleSelectCategory = (categoryId, categoryName) => {
    setSelectedCategory(categoryId);
    setSearchTerm(categoryName);
    setShowDropdown(false);
  };

  const sortedCategories = useMemo(() => {
    if (!categories.length) {
      return [];
    }
    const idToCategory = new Map(
      categories.map((category) => [category.category_id.toString(), category])
    );
    const recentMatches = recentCategoryIds
      .map((id) => idToCategory.get(id))
      .filter(Boolean);
    const remaining = categories.filter(
      (category) => !recentCategoryIds.includes(category.category_id.toString())
    );
    return [...recentMatches, ...remaining];
  }, [categories, recentCategoryIds]);

  // Filter categories based on search term
  const filteredCategories = sortedCategories.filter(category => 
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Check if search term matches any existing category
  const hasExactMatch = categories.some(category => 
    category.name.toLowerCase() === searchTerm.toLowerCase()
  );

  // Find the category name from the categories list
  const categoryName = currentCategory?.name || 
    categories.find(cat => cat.category_id.toString() === selectedCategory)?.name;
  return (
    <>
      {categoryName ? (
        <span 
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={handleShow}
        >
          {categoryName}
        </span>
      ) : (
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleShow}
        >
          Set Category
        </Button>
      )}

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Select Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger">{error}</Alert>
          )}
          <Form>
            {!showCreateForm && (
              <Form.Group className="mb-3 position-relative">
                <Form.Label>Select or type to create a category</Form.Label>
                <Form.Control
                  ref={searchInputRef}
                  type="text"
                  placeholder="Start typing to search or create a category..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => {
                    // Delay hiding dropdown to allow for clicks
                    setTimeout(() => setShowDropdown(false), 200);
                  }}
                />
                {showDropdown && (
                  <div 
                    className="position-absolute w-100 border rounded shadow-sm"
                    style={{ 
                      zIndex: 1000, 
                      maxHeight: '200px', 
                      overflowY: 'auto',
                      backgroundColor: 'var(--card-bg)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  >
                    {filteredCategories.length === 0 && (
                      <div 
                        className="p-2 small"
                        style={{ 
                          backgroundColor: 'var(--bg-secondary)',
                          color: 'var(--text-secondary)'
                        }}
                      >
                        No matching categories found
                      </div>
                    )}
                    
                    {filteredCategories.length > 0 && (
                      filteredCategories.map(category => (
                        <div
                          key={category.category_id}
                          className="p-2 border-bottom d-flex justify-content-between"
                          onClick={() => handleSelectCategory(category.category_id.toString(), category.name)}
                          style={{ 
                            cursor: 'pointer',
                            color: 'var(--text-primary)'
                          }}
                        >
                          <span>{category.name}</span>
                          {selectedCategory === category.category_id.toString() && <span>✓</span>}
                        </div>
                      ))
                    )}
                    
                    {searchTerm.trim() !== '' && !hasExactMatch && (
                      <div 
                        className="p-2 d-flex align-items-center"
                        onClick={handleCreateNewWithSearch}
                        style={{ 
                          cursor: 'pointer', 
                          color: '#6ea8fe'
                        }}
                      >
                        <span className="me-1">+</span> Create new category: "{searchTerm}"
                      </div>
                    )}
                  </div>
                )}
              </Form.Group>
            )}

            {showCreateForm && (
              <>
                <Form.Group className="mt-3">
                  <Form.Label>New Category Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Enter category name"
                    required
                    autoFocus
                  />
                </Form.Group>
                <Form.Group className="mt-3">
                  <Form.Label>Description (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={newCategoryDescription}
                    onChange={(e) => setNewCategoryDescription(e.target.value)}
                    placeholder="Enter category description"
                  />
                </Form.Group>
                <div className="d-flex justify-content-between mt-3">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewCategoryName('');
                      setNewCategoryDescription('');
                    }}
                  >
                    Back to Selection
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleCreateCategory}
                    disabled={loading || !newCategoryName}
                  >
                    {loading ? 'Creating...' : 'Create Category'}
                  </Button>
                </div>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          {!showCreateForm && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CategorySelect; 
